import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, ShoppingCart, Wrench, DollarSign, ArrowUpRight, ArrowDownRight,
  TrendingUp, TrendingDown, AlertTriangle, Play, Loader2, Download, FileText,
  CheckCircle2, ListChecks, HelpCircle, AlertCircle, Layers, CreditCard,
  LayoutGrid, ListTree,
} from 'lucide-react';

import PageHeader from '../../components/ui/PageHeader';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';

import PeriodPicker from '../../components/mec/PeriodPicker';
import MetricTile from '../../components/mec/MetricTile';
import MatchTable from '../../components/mec/drilldown/MatchTable';
import PoAuditSummaryTable from '../../components/mec/drilldown/PoAuditSummaryTable';
import RoGroupedView from '../../components/mec/drilldown/RoGroupedView';
import HelpPill from '../../components/mec/drilldown/HelpPill';
import HealthScoreCard from '../../components/mec/HealthScoreCard';
import { usePeriodState } from '../../components/mec/usePeriodState';
import {
  ALL_CREDIT_PENDING_METHODS,
  CREDIT_PENDING_PO_UNUSED_METHODS,
  CREDIT_PENDING_ZERO_COST_METHODS,
  WARRANTY_CREDIT_PENDING_METHODS,
  CREDIT_PENDING_MANUAL_METHODS,
  CREDIT_PENDING_AUTO_MATCHED_METHODS,
  CORES_OUTSTANDING_METHODS,
} from '../../components/mec/drilldown/MatchRow';

import { listRouting, runClose, getClose, listMatches } from '../../api/mec';
import { formatMoney, formatDate, formatRelative } from '../../utils/formatters';
import { rowsToCsv, downloadCsv, centsToDollarsRaw } from '../../components/mec/csvHelpers';
import { useShop } from '../../hooks/useShop';

// MEC Location Drill-Down (View 3 per MEC_REQUIREMENTS.md §7) — the biggest
// page in the module. Surfaces the per-shop matched ROs, WIP, and Tier 1/2/3
// breakdown for the selected period.
//
// Structure:
//   - Header (shop name + period selector + run button)
//   - Top-line cards: P / A / B / ΔWIP + variance split
//   - Domain tabs: Parts | Sublet
//     - Sub-tabs: Matched | WIP | Tier 1 | Tier 2 (review) | Tier 3 | Ambiguous

function normalizeCloseResult(raw) {
  if (!raw) return null;
  if (raw.parts) return raw;
  const a_total = raw.a_total_cents ?? ((raw.a_used_cents || 0) + (raw.a_pending_return_cents || 0));
  const b_total = raw.b_total_cents ?? ((raw.b_received_cents || 0) + (raw.b_pending_return_cents || 0));
  return {
    close_run_id: raw.close_run_id,
    shop_id: raw.shop_id,
    period_start: raw.period_start,
    period_end_inclusive: raw.period_end_inclusive,
    status: raw.status,
    created_at: raw.created_at,
    finalized_at: raw.finalized_at,
    parts: {
      a_used_cents: raw.a_used_cents || 0,
      a_pending_return_cents: raw.a_pending_return_cents || 0,
      a_total_cents: a_total,
      b_received_cents: raw.b_received_cents || 0,
      b_pending_return_cents: raw.b_pending_return_cents || 0,
      b_total_cents: b_total,
      b0_cents: raw.b0_cents || 0,
      delta_wip_cents: raw.delta_wip_cents ?? (b_total - (raw.b0_cents || 0)),
      p_cents: raw.p_cents || 0,
      variance_cents: raw.variance_cents || 0,
      match_tally: {},
    },
    sublet: null,
  };
}

const TABS = [
  { key: 'matched', label: 'Matched ROs', icon: Wrench },
  { key: 'wip', label: 'WIP ROs', icon: Layers },
  { key: 'tier1', label: 'Tier 1 Matches', icon: CheckCircle2 },
  { key: 'tier2', label: 'Tier 2 Pending', icon: ListChecks },
  { key: 'creditPending', label: 'Credit Pending', icon: CreditCard },
  { key: 'tier3', label: 'Tier 3 Leakage', icon: AlertTriangle },
  { key: 'ambiguous', label: 'Ambiguous', icon: HelpCircle },
];

// D-037: po_unused removed from Tier 3 (moved to Credit Pending family).
const TIER3_SUBS = [
  { key: 'tier_3_po_not_in_tekmetric', label: 'PO Not in Tekmetric', description: 'PO# in QBO memo, but order_number/invoice_number not found.' },
  { key: 'tier_3_no_po', label: 'No PO in Memo', description: 'No PO# parsed from memo and Tier 2 heuristic also failed.' },
];

// D-04x: Tier 1 split into two views — clean PO matches (flat table) vs the
// per-PO total-mismatch audit (two-tier accordion). Both buckets still roll
// up into the Tier 1 tab badge count.
const TIER1_SUBS = [
  {
    key: 'clean',
    label: 'Clean PO Matches',
    methods: ['tier_1_po'],
    description: 'QBO line memos that resolved cleanly to a Tekmetric PO with totals within tolerance.',
  },
  {
    key: 'mismatch',
    label: 'PO Total Mismatch',
    methods: ['tier_1_total_mismatch'],
    description: 'PO# matched but the QBO total differs from Tekmetric loaded cost — drill into a PO to see which vendor is over- or under-billed.',
  },
];

// Phase 4: deep-link bucket key (`?filter=...` from Today / Dashboard) →
// (tab, sub) inside the audit-mode view. When this param is present on
// mount we auto-switch viewMode to 'audit_mode' AND apply the mapping so
// the deep-linked bucket lands the user in the right tab.
const FILTER_TO_TAB = {
  tier_1_total_mismatch: { tab: 'tier1', sub: 'mismatch' },
  ambiguous: { tab: 'ambiguous' },
  credit_pending: { tab: 'creditPending' },
  tier_3_no_po: { tab: 'tier3', sub: 'tier_3_no_po' },
  tier_3_po_not_in_tekmetric: { tab: 'tier3', sub: 'tier_3_po_not_in_tekmetric' },
  tier_2: { tab: 'tier2' },
};

// D-037: Credit Pending breakdown sub-tabs. Each surface uses the method
// arrays exported by MatchRow so legacy method names (pre-D-037 rows) match
// alongside their renamed equivalents.
const CREDIT_PENDING_SUBS = [
  {
    key: 'po_unused',
    label: 'PO Unused',
    description: 'PO in Tek + QBO but no RO attached — part was removed from the RO and is awaiting vendor refund.',
    methods: CREDIT_PENDING_PO_UNUSED_METHODS,
  },
  {
    key: 'zero_cost',
    label: 'Zero Cost',
    description: 'Advisor entered $0 cost in Tek but QBO has a real bill — refund expected. Could be warranty or any vendor refund (defective, recall).',
    methods: CREDIT_PENDING_ZERO_COST_METHODS,
  },
  {
    key: 'warranty',
    label: 'Warranty',
    description: 'RO/job carries the explicit warranty flag — confirmed warranty credit pending.',
    methods: WARRANTY_CREDIT_PENDING_METHODS,
  },
  {
    key: 'manual',
    label: 'Manual',
    description: 'User-tagged as pending credit from the review UI.',
    methods: CREDIT_PENDING_MANUAL_METHODS,
  },
  {
    key: 'auto_matched',
    label: 'Auto-matched',
    description: 'Ordered-but-not-received pop matched to a QBO bill by amount + date (±2 days of order_date). Auto-routed to A_pending_return; refund will offset when it arrives.',
    methods: CREDIT_PENDING_AUTO_MATCHED_METHODS,
  },
  {
    key: 'cores_outstanding',
    label: 'Cores Outstanding',
    description: 'Core deposits paid upfront on received parts (e.g., transmission, alternator, rack & pinion). Awaiting vendor credit memo when the old part is returned. Service advisor shown for accountability; auto-pairs with arriving credit memos.',
    methods: CORES_OUTSTANDING_METHODS,
  },
];

export default function LocationDrillDown() {
  const { shop_id } = useParams();
  const navigate = useNavigate();
  const shopIdNum = Number(shop_id);
  const { currentShopId, switchShop } = useShop();

  // URL ↔ TopBar bidirectional sync. URL is the source of truth on this
  // page (user can deep-link to any shop's drill-down), but the TopBar
  // should reflect the same shop, AND if the user picks a different shop
  // in the TopBar we should navigate to that shop's drill-down.
  //
  // The ref breaks the infinite loop: when URL→TopBar sync fires switchShop,
  // we mark the next currentShopId change as "expected" and skip the
  // URL navigation reaction. Only genuine TopBar clicks trigger navigation.
  const syncingFromURL = useRef(false);

  useEffect(() => {
    // URL changed (mount or back/forward): sync TopBar to URL.
    if (shopIdNum && Number(currentShopId) !== shopIdNum) {
      syncingFromURL.current = true;
      switchShop(shopIdNum);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopIdNum]);

  useEffect(() => {
    // currentShopId changed.
    if (syncingFromURL.current) {
      // This change was caused by our URL→TopBar sync above — ignore.
      syncingFromURL.current = false;
      return;
    }
    // Genuine TopBar click — navigate URL to match.
    if (currentShopId && Number(currentShopId) !== shopIdNum) {
      navigate(`/mec/location/${currentShopId}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentShopId]);

  const [routing, setRouting] = useState([]);
  const [routingLoading, setRoutingLoading] = useState(true);

  // Phase 4: shared period state hook (URL ↔ localStorage ↔ default).
  // Per-shop storage key so each location remembers its own last period.
  const { periodStart, periodEnd, setPeriod } = usePeriodState(`location_${shopIdNum}`);

  const [searchParams] = useSearchParams();

  const [closeResult, setCloseResult] = useState(null);
  const [closeLoading, setCloseLoading] = useState(false);
  const [closeError, setCloseError] = useState(null);
  const [running, setRunning] = useState(false);

  const [domain, setDomain] = useState('parts'); // 'parts' | 'sublet'
  const [activeTab, setActiveTab] = useState('matched');
  const [tier1Sub, setTier1Sub] = useState('clean');
  const [tier3Sub, setTier3Sub] = useState('tier_3_no_po');
  const [creditPendingSub, setCreditPendingSub] = useState('po_unused');

  // Phase 4: view mode toggle. 'per_ro' is the new default (RoGroupedView);
  // 'audit_mode' is the unchanged tab-based breakdown for power users.
  // Per-shop localStorage key so each location remembers its own preference.
  const [viewMode, setViewMode] = useState(() => {
    try {
      return localStorage.getItem(`mec.viewMode.${shopIdNum}`) || 'per_ro';
    } catch {
      return 'per_ro';
    }
  });
  const onViewModeChange = (m) => {
    setViewMode(m);
    try {
      localStorage.setItem(`mec.viewMode.${shopIdNum}`, m);
    } catch {
      // best-effort persistence
    }
  };

  // Phase 4: deep-link `?filter=<bucket>` → flip to Audit Mode + activate
  // the matching tab/sub. Runs once on mount. Strip the param after applying
  // so a subsequent toggle back to Per RO doesn't keep re-firing.
  const filterAppliedRef = useRef(false);
  useEffect(() => {
    if (filterAppliedRef.current) return;
    const filterKey = searchParams.get('filter');
    if (!filterKey) return;
    const mapping = FILTER_TO_TAB[filterKey];
    if (!mapping) return;
    filterAppliedRef.current = true;
    onViewModeChange('audit_mode');
    setActiveTab(mapping.tab);
    if (mapping.tab === 'tier1' && mapping.sub) setTier1Sub(mapping.sub);
    if (mapping.tab === 'tier3' && mapping.sub) setTier3Sub(mapping.sub);
    if (mapping.tab === 'creditPending' && mapping.sub) setCreditPendingSub(mapping.sub);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [matches, setMatches] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [matchesError, setMatchesError] = useState(null);
  // 2026-06-22: re-running a close reuses the same close_run_id, so a
  // useEffect keyed only on close_run_id wouldn't refetch matches. This
  // counter bumps on every successful run/re-run so React refetches and
  // PoAuditAccordion's cache (keyed via React's key prop) busts cleanly.
  const [matchesRefreshKey, setMatchesRefreshKey] = useState(0);

  // Load routing → resolve shop name.
  useEffect(() => {
    let alive = true;
    listRouting()
      .then((data) => {
        if (!alive) return;
        setRouting(data?.shops || []);
      })
      .catch(() => {})
      .finally(() => alive && setRoutingLoading(false));
    return () => { alive = false; };
  }, []);

  const shop = useMemo(
    () => routing.find((s) => s.shop_id === shopIdNum) || { shop_id: shopIdNum, shop_name: `Shop ${shopIdNum}` },
    [routing, shopIdNum]
  );

  // Fetch the close snapshot for this shop+period.
  const reloadClose = useCallback(() => {
    if (!shopIdNum || !periodStart || !periodEnd) return;
    let alive = true;
    setCloseLoading(true);
    setCloseError(null);
    setCloseResult(null);
    getClose(shopIdNum, periodStart, periodEnd)
      .then((data) => alive && setCloseResult(normalizeCloseResult(data)))
      .catch((err) => {
        if (!alive) return;
        if (/404|not found/i.test(err?.message || '')) {
          setCloseResult(null);
        } else {
          setCloseError(err?.message || 'Failed to load close run');
        }
      })
      .finally(() => alive && setCloseLoading(false));
    return () => { alive = false; };
  }, [shopIdNum, periodStart, periodEnd]);

  useEffect(() => {
    return reloadClose();
  }, [reloadClose]);

  const runIt = useCallback(async () => {
    setRunning(true);
    setCloseError(null);
    try {
      const result = await runClose(shopIdNum, periodStart, periodEnd, false);
      setCloseResult(normalizeCloseResult(result));
      // Re-run reuses the same close_run_id, so we must explicitly trigger
      // a match refetch + bust PoAuditAccordion caches keyed off this counter.
      setMatchesRefreshKey((k) => k + 1);
    } catch (err) {
      setCloseError(err?.message || 'Close run failed');
    } finally {
      setRunning(false);
    }
  }, [shopIdNum, periodStart, periodEnd]);

  // Load mec_match rows when we have a close_run_id and the domain changes.
  useEffect(() => {
    if (!closeResult?.close_run_id) {
      setMatches([]);
      return;
    }
    let alive = true;
    setMatchesLoading(true);
    setMatchesError(null);
    listMatches(closeResult.close_run_id, domain)
      .then((data) => {
        if (!alive) return;
        setMatches(data?.matches || data || []);
      })
      .catch((err) => {
        if (!alive) return;
        if (/404|not found/i.test(err?.message || '')) {
          setMatches([]);
        } else {
          setMatchesError(err?.message || 'Failed to load matches');
          setMatches([]);
        }
      })
      .finally(() => alive && setMatchesLoading(false));
    return () => { alive = false; };
  }, [closeResult?.close_run_id, domain, matchesRefreshKey]);

  const onMatchResolved = (matchId, updated) => {
    setMatches((prev) => prev.map((m) => (m.id === matchId ? updated : m)));
  };

  // Compute summary stats from the close.
  const stats = useMemo(() => {
    const dom = closeResult?.[domain] || closeResult?.parts || null;
    if (!dom) return null;
    const p = dom.p_cents || 0;
    const a = dom.a_total_cents ?? ((dom.a_used_cents || 0) + (dom.a_pending_return_cents || 0));
    const b = dom.b_total_cents ?? ((dom.b_received_cents || 0) + (dom.b_pending_return_cents || 0));
    const dWip = dom.delta_wip_cents ?? (b - (dom.b0_cents || 0));
    const variance = dom.variance_cents ?? (p - a - dWip);
    return {
      p, a, b, dWip,
      b0: dom.b0_cents || 0,
      a_used: dom.a_used_cents || 0,
      a_pending_return: dom.a_pending_return_cents || 0,
      variance,
      variance_pct: p > 0 ? (variance / p) * 100 : 0,
      variance_positive: variance > 0 ? variance : 0,
      variance_negative: variance < 0 ? -variance : 0,
    };
  }, [closeResult, domain]);

  // Bucket matches per active tab. D-037 split out the credit-pending family
  // from Tier 2 / Tier 3 into its own tab — Tier 2 Pending here is now just
  // tier_2_heuristic awaiting review; Credit Pending owns the (PO unused +
  // zero cost + warranty + manual) family.
  const filteredMatches = useMemo(() => {
    if (!matches?.length) return [];
    switch (activeTab) {
      case 'matched':
        return matches.filter((m) => m.bucket === 'matched_cogs');
      case 'wip':
        return matches.filter((m) => m.bucket === 'wip');
      case 'tier1': {
        // Tier 1 split: 'clean' = tier_1_po flat table. 'mismatch' = handled
        // by PoAuditSummaryTable (independent fetch), so we just return the
        // matching rows for completeness / consistency.
        const sub = TIER1_SUBS.find((s) => s.key === tier1Sub);
        const methodSet = sub ? sub.methods : ['tier_1_po', 'tier_1_total_mismatch'];
        return matches.filter((m) => methodSet.includes(m.matched_method));
      }
      case 'tier2':
        return matches.filter((m) => (
          m.matched_method === 'tier_2_heuristic' && m.review_status === 'pending_review'
        ));
      case 'creditPending': {
        const sub = CREDIT_PENDING_SUBS.find((s) => s.key === creditPendingSub);
        const methodSet = sub ? sub.methods : ALL_CREDIT_PENDING_METHODS;
        return matches.filter((m) => methodSet.includes(m.matched_method));
      }
      case 'tier3':
        return matches.filter((m) => m.matched_method === tier3Sub);
      case 'ambiguous':
        return matches.filter((m) => m.matched_method === 'ambiguous');
      default:
        return matches;
    }
  }, [matches, activeTab, tier1Sub, tier3Sub, creditPendingSub]);

  // Build summary RO list (group matched_cogs/wip by matched_ro_id).
  const groupedROs = useMemo(() => {
    const target = activeTab === 'matched' ? 'matched_cogs' : activeTab === 'wip' ? 'wip' : null;
    if (!target) return null;
    const byRo = {};
    for (const m of matches) {
      if (m.bucket !== target) continue;
      const key = m.matched_ro_id || '_no_ro';
      if (!byRo[key]) {
        byRo[key] = {
          ro_id: m.matched_ro_id,
          ro_number: m.matched_ro_number,
          parts_cents: 0,
          line_count: 0,
          latest_date: null,
        };
      }
      // Defensive: backfill ro_number if a later row has it and the first didn't.
      if (!byRo[key].ro_number && m.matched_ro_number) {
        byRo[key].ro_number = m.matched_ro_number;
      }
      byRo[key].parts_cents += m.qbo_amount_cents || 0;
      byRo[key].line_count += 1;
      const d = m.matched_date || m.qbo_date;
      if (d && (!byRo[key].latest_date || d > byRo[key].latest_date)) {
        byRo[key].latest_date = d;
      }
    }
    return Object.values(byRo).sort((a, b) => b.parts_cents - a.parts_cents);
  }, [matches, activeTab]);

  const exportROCsv = () => {
    if (!groupedROs) return;
    const csv = rowsToCsv(groupedROs, [
      { key: 'ro_number', label: 'RO #', accessor: (r) => r.ro_number || r.ro_id || '— no RO —' },
      { key: 'line_count', label: '# QBO Lines' },
      { key: 'parts_cents', label: 'Parts Cost', accessor: (r) => centsToDollarsRaw(r.parts_cents) },
      { key: 'latest_date', label: 'Latest Date' },
    ]);
    downloadCsv(
      `mec-${domain}-${activeTab}-shop${shopIdNum}-${periodStart}-${periodEnd}.csv`,
      csv
    );
  };

  return (
    <div>
      <PageHeader
        title={shop.shop_name || `Shop ${shopIdNum}`}
        description={`Drill-down for ${domain === 'parts' ? 'Parts Purchases' : 'Sublet'}. Click a tab to see matched ROs, WIP detail, or each tier's contribution to variance.`}
        breadcrumbs={[
          { label: 'Month-End Close', href: '/mec' },
          { label: 'Locations', href: '/mec/locations' },
          { label: shop.shop_name || `Shop ${shopIdNum}` },
        ]}
        actions={
          <>
            <Button
              variant="ghost"
              size="md"
              leftIcon={<ArrowLeft size={15} />}
              onClick={() => navigate('/mec/locations')}
            >
              Locations
            </Button>
            <Button
              variant="primary"
              size="md"
              leftIcon={running ? null : <Play size={15} />}
              loading={running}
              onClick={runIt}
              disabled={running}
            >
              {running ? 'Running…' : closeResult ? 'Re-run close' : 'Run close'}
            </Button>
          </>
        }
      />

      <Card className="mb-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <PeriodPicker start={periodStart} end={periodEnd} onChange={(s, e) => setPeriod(s, e)} />
          {closeResult?.created_at && (
            <div className="text-[11px] text-[var(--color-warm-500)]">
              Last run {formatRelative(closeResult.created_at)}
              {closeResult.status && (
                <Badge tone={closeResult.status === 'finalized' ? 'success' : 'cream'} size="xs" className="ml-2">
                  {closeResult.status}
                </Badge>
              )}
            </div>
          )}
        </div>
      </Card>

      {closeError && (
        <Card className="mb-5">
          <div className="flex items-start gap-3 py-1 text-[var(--color-danger-700)]">
            <AlertTriangle size={18} className="shrink-0 mt-0.5" />
            <div className="text-[14px]">{closeError}</div>
          </div>
        </Card>
      )}

      {/* Top-line cards. Need close run; otherwise empty state. */}
      {!closeResult && !closeLoading && !running && (
        <Card className="mb-5">
          <div className="flex flex-col items-center text-center py-10">
            <DollarSign size={28} className="text-[var(--color-warm-400)] mb-3" />
            <div className="text-[14px] font-semibold text-[var(--color-warm-900)]">
              No close run for this period.
            </div>
            <div className="text-[12px] text-[var(--color-warm-500)] mt-1 max-w-md mb-4">
              Run a draft close to populate the matched ROs, WIP detail, and Tier 1/2/3 breakdown for {formatDate(periodStart)} – {formatDate(periodEnd)}.
            </div>
            <Button variant="primary" size="md" leftIcon={<Play size={15} />} onClick={runIt} loading={running}>
              Run close now
            </Button>
          </div>
        </Card>
      )}

      {(closeResult || closeLoading) && (
        <>
          {/* Phase 4: Health Score headline (parts domain only). Renders a
              skeleton while loading and a "no data" state when closeResult is
              null. Defensive against partial payloads. */}
          {domain === 'parts' && (
            <div className="mb-5">
              <div className="flex items-center gap-1.5 mb-2">
                <span className="text-[11px] uppercase tracking-[1px] font-bold text-[var(--color-warm-500)]">
                  Close Health
                </span>
                <HelpPill topic="health_score" />
              </div>
              <HealthScoreCard
                closeResult={closeResult}
                matches={Array.isArray(matches) ? matches : []}
                loading={closeLoading}
              />
            </div>
          )}

          {/* Domain + View Mode toggles */}
          <div className="flex items-center gap-x-6 gap-y-2 mb-4 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-[11px] uppercase tracking-[1px] font-bold text-[var(--color-warm-500)] mr-1">
                Domain:
              </span>
              <div className="inline-flex items-center bg-[var(--color-cream-100)] rounded-[var(--radius-full)] p-1 gap-0.5 border border-[var(--color-warm-100)]">
                {[
                  { key: 'parts', label: 'Parts' },
                  { key: 'sublet', label: 'Sublet' },
                ].map((d) => {
                  const active = domain === d.key;
                  return (
                    <button
                      key={d.key}
                      type="button"
                      onClick={() => { setDomain(d.key); setActiveTab('matched'); }}
                      className={`
                        px-3 h-7 text-[12px] font-semibold rounded-[var(--radius-full)] transition-colors
                        ${active
                          ? 'bg-[var(--color-warm-900)] text-[var(--color-cream-50)] shadow-[var(--shadow-xs)]'
                          : 'text-[var(--color-warm-600)] hover:text-[var(--color-warm-900)]'}
                      `}
                    >
                      {d.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-[11px] uppercase tracking-[1px] font-bold text-[var(--color-warm-500)] mr-1">
                View:
              </span>
              <div className="inline-flex items-center bg-[var(--color-cream-100)] rounded-[var(--radius-full)] p-1 gap-0.5 border border-[var(--color-warm-100)]">
                {[
                  { key: 'per_ro', label: 'Per RO', icon: LayoutGrid, topic: 'per_ro_view' },
                  { key: 'audit_mode', label: 'Audit Mode', icon: ListTree, topic: 'audit_mode' },
                ].map((v) => {
                  const active = viewMode === v.key;
                  const Icon = v.icon;
                  return (
                    <button
                      key={v.key}
                      type="button"
                      onClick={() => onViewModeChange(v.key)}
                      className={`
                        inline-flex items-center gap-1.5 px-3 h-7 text-[12px] font-semibold rounded-[var(--radius-full)] transition-colors
                        ${active
                          ? 'bg-[var(--color-warm-900)] text-[var(--color-cream-50)] shadow-[var(--shadow-xs)]'
                          : 'text-[var(--color-warm-600)] hover:text-[var(--color-warm-900)]'}
                      `}
                    >
                      <Icon size={12} />
                      {v.label}
                    </button>
                  );
                })}
              </div>
              <HelpPill topic={viewMode === 'per_ro' ? 'per_ro_view' : 'audit_mode'} />
            </div>

            {domain === 'sublet' && (!closeResult?.sublet) && (
              <span className="text-[11px] text-[var(--color-warm-500)] italic">
                Sublet snapshot may not be populated yet — re-run if numbers look empty.
              </span>
            )}
          </div>

          {/* Top-line cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <MetricTile
              label="Purchases (P)"
              value={formatMoney((stats?.p || 0) / 100)}
              sub={`QBO ${domain === 'sublet' ? 'sublet' : 'parts purchases'} in period`}
              accent="neutral"
              icon={<ShoppingCart />}
              loading={closeLoading}
            />
            <MetricTile
              label="Matched (A)"
              value={formatMoney((stats?.a || 0) / 100)}
              sub={`Used ${formatMoney((stats?.a_used || 0) / 100)}${stats?.a_pending_return ? ` + return-pending ${formatMoney(stats.a_pending_return / 100)}` : ''}`}
              accent="recovery"
              icon={<Wrench />}
              loading={closeLoading}
            />
            <MetricTile
              label="WIP (B)"
              value={formatMoney((stats?.b || 0) / 100)}
              sub="Received & attached to open ROs"
              accent="insight"
              icon={<DollarSign />}
              loading={closeLoading}
            />
            <MetricTile
              label="ΔWIP"
              value={(stats?.dWip >= 0 ? '+' : '') + formatMoney((stats?.dWip || 0) / 100)}
              sub={`B₀ ${formatMoney((stats?.b0 || 0) / 100)} → B`}
              accent={stats?.dWip >= 0 ? 'action' : 'recovery'}
              icon={stats?.dWip >= 0 ? <TrendingUp /> : <TrendingDown />}
              loading={closeLoading}
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            <MetricTile
              label="Positive Variance (Leakage)"
              value={formatMoney((stats?.variance_positive || 0) / 100)}
              sub="QBO over Matched + WIP — investigate"
              accent={
                stats?.p > 0 && (stats.variance_positive / stats.p) * 100 > 10 ? 'danger'
                  : stats?.p > 0 && (stats.variance_positive / stats.p) * 100 > 5 ? 'action'
                    : 'neutral'
              }
              icon={<ArrowUpRight />}
              loading={closeLoading}
            />
            <MetricTile
              label="Negative Variance (Recovery)"
              value={formatMoney((stats?.variance_negative || 0) / 100)}
              sub="Refunds, credits, prior-period offsets"
              accent="recovery"
              icon={<ArrowDownRight />}
              loading={closeLoading}
            />
            <MetricTile
              label="Net Variance %"
              value={`${(stats?.variance_pct || 0) >= 0 ? '+' : ''}${(stats?.variance_pct || 0).toFixed(1)}%`}
              sub={`Net ${formatMoney((stats?.variance || 0) / 100)} ÷ P`}
              accent={Math.abs(stats?.variance_pct || 0) > 10 ? 'danger' : Math.abs(stats?.variance_pct || 0) > 5 ? 'action' : 'neutral'}
              icon={<DollarSign />}
              loading={closeLoading}
            />
          </div>

          {/* Match list errors (shared by both view modes) */}
          {matchesError && (
            <Card className="mb-4">
              <div className="flex items-start gap-3 py-1 text-[var(--color-danger-700)]">
                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                <div className="text-[13px]">{matchesError}</div>
              </div>
            </Card>
          )}

          {matchesLoading && (
            <Card>
              <div className="flex items-center gap-2 justify-center py-8 text-[12px] text-[var(--color-warm-500)]">
                <Loader2 size={14} className="animate-spin" />
                Loading {domain} matches…
              </div>
            </Card>
          )}

          {/* Phase 4: Per-RO default view. RoGroupedView handles its own
              empty / filtered-empty states, so we render it whenever we have
              a close result (matches may be []). */}
          {!matchesLoading && viewMode === 'per_ro' && (
            <RoGroupedView
              key={`ro-grouped-${closeResult?.close_run_id}-${matchesRefreshKey}`}
              matches={matches || []}
              onMatchResolved={onMatchResolved}
              shopIdNum={shopIdNum}
              periodStart={periodStart}
              periodEnd={periodEnd}
              domain={domain}
              closeRunId={closeResult?.close_run_id}
              loading={false}
            />
          )}

          {/* Audit Mode: byte-equivalent to the prior tab-based UI. Wrapped
              in a conditional so power users can still drill by matcher
              bucket. */}
          {viewMode === 'audit_mode' && (
            <>
              {/* Tab strip */}
              <Card padded={false} className="mb-4">
                <div className="flex items-center border-b border-[var(--color-warm-100)] overflow-x-auto">
                  {TABS.map((t) => {
                    const active = activeTab === t.key;
                    const Icon = t.icon;
                    const count = bucketCount(matches, t.key, tier3Sub);
                    const helpTopic = (
                      t.key === 'matched' ? 'matched'
                      : t.key === 'wip' ? 'wip'
                      : t.key === 'tier1' ? 'tier1'
                      : t.key === 'tier2' ? 'tier2'
                      : t.key === 'creditPending' ? 'creditPending'
                      : t.key === 'tier3' ? 'tier3'
                      : t.key === 'ambiguous' ? 'ambiguous'
                      : null
                    );
                    return (
                      <div
                        key={t.key}
                        className={`
                          inline-flex items-center gap-1 px-2 border-b-2 transition-colors
                          ${active
                            ? 'border-[var(--color-warm-900)]'
                            : 'border-transparent'}
                        `}
                      >
                        <button
                          type="button"
                          onClick={() => setActiveTab(t.key)}
                          className={`
                            inline-flex items-center gap-2 px-2 h-11 text-[13px] font-semibold whitespace-nowrap transition-colors
                            ${active
                              ? 'text-[var(--color-warm-900)]'
                              : 'text-[var(--color-warm-500)] hover:text-[var(--color-warm-800)]'}
                          `}
                        >
                          <Icon size={14} />
                          {t.label}
                          {count > 0 && (
                            <Badge
                              tone={
                                t.key === 'tier3' ? 'danger'
                                  : t.key === 'tier2' ? 'action'
                                    : t.key === 'creditPending' ? 'insight'
                                      : 'neutral'
                              }
                              size="xs"
                            >
                              {count}
                            </Badge>
                          )}
                        </button>
                        {helpTopic && <HelpPill topic={helpTopic} />}
                      </div>
                    );
                  })}
                </div>
              </Card>

          {!matchesLoading && (
            <>
              {(activeTab === 'matched' || activeTab === 'wip') && (
                <RoBucketView
                  bucketKind={activeTab}
                  groupedROs={groupedROs || []}
                  matches={filteredMatches}
                  onMatchResolved={onMatchResolved}
                  shopIdNum={shopIdNum}
                  domain={domain}
                  periodStart={periodStart}
                  periodEnd={periodEnd}
                  exportROCsv={exportROCsv}
                />
              )}

              {activeTab === 'tier1' && (
                <>
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    {TIER1_SUBS.map((s) => {
                      const active = tier1Sub === s.key;
                      const count = matches.filter((m) => s.methods.includes(m.matched_method)).length;
                      return (
                        <button
                          key={s.key}
                          type="button"
                          onClick={() => setTier1Sub(s.key)}
                          className={`
                            inline-flex items-center gap-1.5 px-3 h-8 text-[12px] font-semibold rounded-[var(--radius-full)] border transition-colors
                            ${active
                              ? 'bg-[var(--color-recovery-50)] text-[var(--color-recovery-700)] border-[var(--color-recovery-300)]'
                              : 'bg-[var(--color-cream-50)] text-[var(--color-warm-600)] border-[var(--color-warm-200)] hover:border-[var(--color-warm-300)]'}
                          `}
                        >
                          {s.label}
                          {count > 0 && (
                            <Badge tone={active ? 'recovery' : 'neutral'} size="xs">{count}</Badge>
                          )}
                        </button>
                      );
                    })}
                    <HelpPill topic="tier1" />
                  </div>
                  {tier1Sub === 'clean' && (
                    <MatchTable
                      rows={filteredMatches}
                      title="Tier 1 — Clean PO Matches"
                      description={TIER1_SUBS.find((s) => s.key === 'clean').description}
                      emptyMessage="No clean Tier 1 PO matches for this period."
                      csvFilename={`mec-tier1-clean-${domain}-shop${shopIdNum}-${periodStart}-${periodEnd}.csv`}
                    />
                  )}
                  {tier1Sub === 'mismatch' && (
                    <PoAuditSummaryTable
                      closeRunId={closeResult.close_run_id}
                      method="tier_1_total_mismatch"
                      domain={domain}
                      title="Tier 1 — PO Total Mismatch"
                      description={TIER1_SUBS.find((s) => s.key === 'mismatch').description}
                      emptyMessage="No PO total mismatches for this period — clean close on Tier 1."
                      csvFilename={`mec-tier1-mismatch-${domain}-shop${shopIdNum}-${periodStart}-${periodEnd}.csv`}
                    />
                  )}
                </>
              )}

              {activeTab === 'tier2' && (
                <MatchTable
                  rows={filteredMatches}
                  title="Tier 2 — Pending heuristic matches"
                  description="Vendor + amount + date matches awaiting your review (Mode A queue). Confirm to add to A/WIP, reject to keep as leakage."
                  emptyMessage="No Tier 2 matches awaiting review in this period."
                  csvFilename={`mec-tier2-${domain}-shop${shopIdNum}-${periodStart}-${periodEnd}.csv`}
                  onMatchResolved={onMatchResolved}
                />
              )}

              {activeTab === 'creditPending' && (
                <>
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    {CREDIT_PENDING_SUBS.map((s) => {
                      const active = creditPendingSub === s.key;
                      const count = matches.filter((m) => s.methods.includes(m.matched_method)).length;
                      return (
                        <button
                          key={s.key}
                          type="button"
                          onClick={() => setCreditPendingSub(s.key)}
                          className={`
                            inline-flex items-center gap-1.5 px-3 h-8 text-[12px] font-semibold rounded-[var(--radius-full)] border transition-colors
                            ${active
                              ? 'bg-[var(--color-insight-50)] text-[var(--color-insight-700)] border-[var(--color-insight-300)]'
                              : 'bg-[var(--color-cream-50)] text-[var(--color-warm-600)] border-[var(--color-warm-200)] hover:border-[var(--color-warm-300)]'}
                          `}
                        >
                          {s.label}
                          {count > 0 && (
                            <Badge tone={active ? 'insight' : 'neutral'} size="xs">{count}</Badge>
                          )}
                        </button>
                      );
                    })}
                    <HelpPill topic="creditPending" />
                  </div>
                  <MatchTable
                    rows={filteredMatches}
                    title={`Credit Pending — ${CREDIT_PENDING_SUBS.find((s) => s.key === creditPendingSub)?.label}`}
                    description={CREDIT_PENDING_SUBS.find((s) => s.key === creditPendingSub)?.description}
                    emptyMessage="No rows in this sub-bucket for this period."
                    csvFilename={`mec-credit-pending-${creditPendingSub}-${domain}-shop${shopIdNum}-${periodStart}-${periodEnd}.csv`}
                    onMatchResolved={onMatchResolved}
                  />
                </>
              )}

              {activeTab === 'tier3' && (
                <>
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    {TIER3_SUBS.map((s) => {
                      const active = tier3Sub === s.key;
                      const count = matches.filter((m) => m.matched_method === s.key).length;
                      const subHelpTopic = s.key === 'tier_3_no_po'
                        ? 'tier3_no_po'
                        : s.key === 'tier_3_po_not_in_tekmetric'
                          ? 'tier3_po_not_in_tekmetric'
                          : null;
                      return (
                        <span key={s.key} className="inline-flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => setTier3Sub(s.key)}
                            className={`
                              inline-flex items-center gap-1.5 px-3 h-8 text-[12px] font-semibold rounded-[var(--radius-full)] border transition-colors
                              ${active
                                ? 'bg-[var(--color-danger-50)] text-[var(--color-danger-700)] border-[var(--color-danger-300)]'
                                : 'bg-[var(--color-cream-50)] text-[var(--color-warm-600)] border-[var(--color-warm-200)] hover:border-[var(--color-warm-300)]'}
                            `}
                          >
                            {s.label}
                            {count > 0 && (
                              <Badge tone={active ? 'danger' : 'neutral'} size="xs">{count}</Badge>
                            )}
                          </button>
                          {subHelpTopic && <HelpPill topic={subHelpTopic} />}
                        </span>
                      );
                    })}
                  </div>
                  <MatchTable
                    rows={filteredMatches}
                    title={`Tier 3 — ${TIER3_SUBS.find((s) => s.key === tier3Sub)?.label}`}
                    description={TIER3_SUBS.find((s) => s.key === tier3Sub)?.description}
                    emptyMessage="No rows in this sub-bucket for this period."
                    csvFilename={`mec-${tier3Sub}-${domain}-shop${shopIdNum}-${periodStart}-${periodEnd}.csv`}
                  />
                </>
              )}

              {activeTab === 'ambiguous' && (
                <PoAuditSummaryTable
                  closeRunId={closeResult.close_run_id}
                  method="ambiguous"
                  domain={domain}
                  title="Ambiguous — multiple Tekmetric candidates"
                  description="QBO sum exceeded a single Tekmetric PO, or several POs match the vendor + amount + date window. Drill into a PO to see per-vendor reconciliation."
                  emptyMessage="No ambiguous matches for this period."
                  csvFilename={`mec-ambiguous-${domain}-shop${shopIdNum}-${periodStart}-${periodEnd}.csv`}
                />
              )}
            </>
          )}
            </>
          )}
        </>
      )}
    </div>
  );
}

function bucketCount(matches, tabKey, tier3Sub) {
  if (!matches?.length) return 0;
  switch (tabKey) {
    case 'matched': return matches.filter((m) => m.bucket === 'matched_cogs').length;
    case 'wip': return matches.filter((m) => m.bucket === 'wip').length;
    case 'tier1': return matches.filter((m) => m.matched_method === 'tier_1_po' || m.matched_method === 'tier_1_total_mismatch').length;
    // D-037: Tier 2 Pending is now ONLY tier_2_heuristic in pending_review.
    // Credit-pending family has its own tab.
    case 'tier2': return matches.filter((m) => (
      m.matched_method === 'tier_2_heuristic' && m.review_status === 'pending_review'
    )).length;
    case 'creditPending': return matches.filter((m) => ALL_CREDIT_PENDING_METHODS.includes(m.matched_method)).length;
    // D-037: po_unused moved out of Tier 3; count only the remaining T3 subs.
    case 'tier3': return matches.filter((m) => (
      m.matched_method === 'tier_3_po_not_in_tekmetric' || m.matched_method === 'tier_3_no_po'
    )).length;
    case 'ambiguous': return matches.filter((m) => m.matched_method === 'ambiguous').length;
    default: return 0;
  }
}

// Two-pane view for Matched / WIP tabs: RO summary on top, contributing
// match rows below. The RO grouping is computed in the parent — we just
// render it.
function RoBucketView({
  bucketKind,
  groupedROs,
  matches,
  onMatchResolved,
  shopIdNum,
  domain,
  periodStart,
  periodEnd,
  exportROCsv,
}) {
  return (
    <div className="space-y-4">
      <Card padded={false}>
        <div className="px-5 pt-5 pb-3 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-[15px] font-semibold text-[var(--color-warm-900)]">
              {bucketKind === 'matched' ? 'Matched ROs' : 'WIP ROs'}
            </h3>
            <p className="text-[12px] text-[var(--color-warm-500)] mt-0.5">
              {bucketKind === 'matched'
                ? 'Closed ROs that absorbed parts cost in this period.'
                : 'Open ROs at period-end with parts received but not yet posted.'}
            </p>
          </div>
          <Button
            size="sm"
            variant="secondary"
            leftIcon={<Download size={12} />}
            onClick={exportROCsv}
            disabled={!groupedROs.length}
          >
            CSV
          </Button>
        </div>

        <div className="overflow-x-auto border-t border-[var(--color-warm-100)]">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="bg-[var(--color-cream-100)] border-b border-[var(--color-warm-100)]">
                <th className="px-4 py-2.5 text-left text-[10px] font-bold uppercase tracking-[1px] text-[var(--color-warm-500)]">RO #</th>
                <th className="px-4 py-2.5 text-right text-[10px] font-bold uppercase tracking-[1px] text-[var(--color-warm-500)]"># Lines</th>
                <th className="px-4 py-2.5 text-right text-[10px] font-bold uppercase tracking-[1px] text-[var(--color-warm-500)]">Parts Cost</th>
                <th className="px-4 py-2.5 text-right text-[10px] font-bold uppercase tracking-[1px] text-[var(--color-warm-500)]">Latest Date</th>
              </tr>
            </thead>
            <tbody>
              {groupedROs.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-10 text-center text-[12px] text-[var(--color-warm-500)] italic">
                    {bucketKind === 'wip'
                      ? 'No open ROs in this period had QBO transactions land in WIP. (WIP $ on the cards above comes from parts received in prior periods that are still on open tickets.)'
                      : 'No ROs in this bucket for this shop and period.'}
                  </td>
                </tr>
              )}
              {groupedROs.map((r, i) => (
                <tr key={i} className="border-b border-[var(--color-warm-100)] last:border-b-0 hover:bg-[var(--color-cream-100)]/50">
                  <td className="px-4 py-3 align-middle">
                    {(r.ro_number || r.ro_id) ? (
                      <span className="text-[13px] font-semibold mono-nums text-[var(--color-warm-900)]">
                        RO #{r.ro_number || r.ro_id}
                      </span>
                    ) : (
                      <span className="text-[12px] italic text-[var(--color-warm-500)]">— no RO id —</span>
                    )}
                  </td>
                  <td className="px-4 py-3 align-middle text-right tabular-nums text-[var(--color-warm-700)]">
                    {r.line_count}
                  </td>
                  <td className="px-4 py-3 align-middle text-right tabular-nums font-semibold text-[var(--color-warm-900)]">
                    {formatMoney(r.parts_cents / 100)}
                  </td>
                  <td className="px-4 py-3 align-middle text-right text-[var(--color-warm-600)]">
                    {r.latest_date ? formatDate(r.latest_date) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Underlying match rows */}
      <MatchTable
        rows={matches}
        title={bucketKind === 'matched' ? 'Contributing matches' : 'Contributing matches (WIP)'}
        description="QBO lines that contributed to the bucket above."
        emptyMessage="No contributing matches found for this bucket."
        csvFilename={`mec-${bucketKind}-matches-${domain}-shop${shopIdNum}-${periodStart}-${periodEnd}.csv`}
        onMatchResolved={onMatchResolved}
      />
    </div>
  );
}
